import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
} from "react";

export interface LoadingStep {
    name: string;
    expectedDuration: number; // in milliseconds
    weight: number; // relative weight of this step in overall progress
}

export interface LoadingContextType {
    isLoading: boolean;
    progress: number;
    message: string;
    currentStep: number;
    startLoading: (steps: LoadingStep[]) => void;
    updateStep: (stepIndex: number, message?: string) => void;
    stopLoading: () => void;
    handleTimeBasedLoading: <T>(
        operation: Promise<T>,
        config: TimeBasedConfig
    ) => Promise<T>;
    handleStepBasedLoading: <T>(
        operation: Promise<T>,
        config: StepBasedConfig
    ) => Promise<T>;
}

export interface TimeBasedConfig {
    totalDuration: number;
    messages?: string[];
    onComplete?: () => void;
}

export interface StepBasedConfig {
    steps: LoadingStep[];
    onStepComplete?: (stepIndex: number) => void;
    onComplete?: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
    children: React.ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({
    children,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [message, setMessage] = useState("");
    const [currentStep, setCurrentStep] = useState(0);
    const currentStepRef = useRef(0);

    const stepsRef = useRef<LoadingStep[]>([]);
    const startTimeRef = useRef<number>(0);
    const progressInterval = useRef<NodeJS.Timeout | null>(null);
    const accumulatedTimeRef = useRef(0);
    const stepStartRef = useRef(0);

    useEffect(() => {
        // Update ref when currentStep changes
        currentStepRef.current = currentStep;
    }, [currentStep]);

    const calculateProgress = (
        currentStepIndex: number,
        totalElapsedMs: number
    ) => {
        const steps = stepsRef.current;
        let totalProgress = 0;

        // 1) Add full weight for all completed steps
        for (let i = 0; i < currentStepIndex; i++) {
            totalProgress += steps[i].weight;
        }

        // 2) Add partial progress for the current step
        if (currentStepIndex < steps.length) {
            // Total expected duration for previous steps
            const previousStepsDuration = steps
                .slice(0, currentStepIndex)
                .reduce((sum, s) => sum + s.expectedDuration, 0);

            // Calculate time spent in current step
            const timeInCurrentStep = totalElapsedMs - previousStepsDuration;
            const current = steps[currentStepIndex];
            if (timeInCurrentStep > 0) {
                const stepProgress = Math.min(
                    timeInCurrentStep / current.expectedDuration,
                    1
                );
                totalProgress += current.weight * stepProgress;
            }
        }

        // Return as percentage (clamped near 100%)
        return Math.min(totalProgress * 100, 99.9);
    };

    const startLoading = (steps: LoadingStep[]) => {
        // Normalize weights so they sum to 1
        const totalWeight = steps.reduce((sum, step) => sum + step.weight, 0);
        const normalizedSteps = steps.map((step) => ({
            ...step,
            weight: step.weight / totalWeight,
        }));

        stepsRef.current = normalizedSteps;
        accumulatedTimeRef.current = 0;
        startTimeRef.current = Date.now();
        setCurrentStep(0);
        setProgress(0);
        setMessage(steps[0].name);
        setIsLoading(true);

        // Start progress tracking
        progressInterval.current = setInterval(() => {
            const totalElapsedSoFar =
                accumulatedTimeRef.current +
                (Date.now() - stepStartRef.current);
            const stepIndex = currentStepRef.current;
            const newProgress = calculateProgress(stepIndex, totalElapsedSoFar);
            setProgress(newProgress);
        }, 100);
    };

    const updateStep = (stepIndex: number, customMessage?: string) => {
        if (stepIndex >= stepsRef.current.length) {
            stopLoading();
            return;
        }
        const now = Date.now();
        accumulatedTimeRef.current += now - stepStartRef.current;

        setCurrentStep(stepIndex);
        startTimeRef.current = now; // Reset timer for the new step
        setMessage(customMessage || stepsRef.current[stepIndex].name);
    };

    const stopLoading = () => {
        if (progressInterval.current) {
            clearInterval(progressInterval.current);
        }
        setProgress(100);
        setTimeout(() => {
            setIsLoading(false);
            setProgress(0);
            setMessage("");
            setCurrentStep(0);
            stepsRef.current = [];
        }, 500);
    };

    const handleTimeBasedLoading = async <T,>(
        operation: Promise<T>,
        config: TimeBasedConfig
    ): Promise<T> => {
        const { totalDuration, messages = [], onComplete } = config;

        if (messages.length === 0) {
            const steps: LoadingStep[] = [
                {
                    name: "Processing...",
                    expectedDuration: totalDuration,
                    weight: 1,
                },
            ];
            startLoading(steps);
        } else {
            const stepDuration = totalDuration / messages.length;
            const steps: LoadingStep[] = messages.map((msg) => ({
                name: msg,
                expectedDuration: stepDuration,
                weight: 1 / messages.length,
            }));
            startLoading(steps);

            const messageUpdateInterval = stepDuration;
            let currentMessageIndex = 0;

            const messageInterval = setInterval(() => {
                currentMessageIndex++;
                if (currentMessageIndex < messages.length) {
                    updateStep(
                        currentMessageIndex,
                        messages[currentMessageIndex]
                    );
                }
            }, messageUpdateInterval);

            // Clear the interval when the operation completes
            operation.finally(() => {
                clearInterval(messageInterval);
            });
        }

        try {
            const result = await operation;
            console.log("Operation result:", result);
            onComplete?.();
            stopLoading();
            return result;
        } catch (error) {
            stopLoading();
            throw error;
        }
    };

    const handleStepBasedLoading = async <T,>(
        operation: Promise<T>,
        config: StepBasedConfig
    ): Promise<T> => {
        const { steps, onComplete } = config;

        if (steps.length === 0) {
            const defaultSteps: LoadingStep[] = [
                { name: "Processing...", expectedDuration: 1000, weight: 1 },
            ];
            startLoading(defaultSteps);
        } else {
            startLoading(steps);
        }

        try {
            const result = await operation;
            onComplete?.();
            stopLoading();
            return result;
        } catch (error) {
            stopLoading();
            throw error;
        }
    };

    useEffect(() => {
        return () => {
            if (progressInterval.current) {
                clearInterval(progressInterval.current);
            }
        };
    }, []);

    return (
        <LoadingContext.Provider
            value={{
                isLoading,
                progress,
                message,
                currentStep,
                startLoading,
                updateStep,
                stopLoading,
                handleTimeBasedLoading,
                handleStepBasedLoading,
            }}>
            {children}
        </LoadingContext.Provider>
    );
};

export const useLoading = () => {
    const context = useContext(LoadingContext);
    if (!context) {
        throw new Error("useLoading must be used within a LoadingProvider");
    }
    return context;
};
